import * as React from 'react';
import { useNotificationsContext } from '../contexts/NotificationsContext';

// A toast is DISPOSABLE: it auto-closes after this long, or when the user clicks x. The persistent
// "still running" signal is the bell icon's spinner - the toast is only the transient announcement.
// Keep in sync with the `job-chip-countdown` animation duration in notifications.css.
const AUTO_CLOSE_MS = 5_000;
// Remember which job events we've already announced, so a re-poll or a page reload never re-toasts the
// same event (a closed toast stays closed). Bounded - older jobs drop off the notifications list anyway.
const ANNOUNCED_KEY = 'protoss:announced-job-toasts';
const ANNOUNCED_CAP = 200;

interface Toast {
  key: string; // `${uuid}:running` | `${uuid}:done` - unique per announced event
  title: string;
  meta: string;
  variant: string; // running | success | error | warning | info - drives dot/border/bar color
}

function loadAnnounced(): Set<string> {
  try {
    const raw = window.localStorage.getItem(ANNOUNCED_KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function persistAnnounced(set: Set<string>): void {
  try {
    window.localStorage.setItem(ANNOUNCED_KEY, JSON.stringify([...set].slice(-ANNOUNCED_CAP)));
  } catch {
    // storage unavailable/full - dedupe just won't survive this session.
  }
}

/** Total elapsed since the job started (createdAt stays the start time even after a status update). */
function elapsed(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/**
 * Floating, vertically-stacked disposable toasts below the header (top-right). One toast announces a
 * background job STARTING, another announces it FINISHING; each self-destructs after a few seconds (a
 * depleting bar shows the countdown) or when the user clicks x. The persistent "is it still running"
 * cue is the header bell's spinner, and the bell panel is the durable log. Reads the SAME shared
 * notifications as the bell. Tokens-only styling lives in styles/notifications.css.
 */
export function JobToaster(): React.ReactElement | null {
  const { enabled, notifications } = useNotificationsContext();
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const announcedRef = React.useRef<Set<string>>(new Set());
  const timersRef = React.useRef<Map<string, number>>(new Map());
  // Seed the announced set from localStorage once (so a closed/expired toast never reappears on reload).
  const seededRef = React.useRef(false);
  if (!seededRef.current) {
    seededRef.current = true;
    loadAnnounced().forEach((k) => announcedRef.current.add(k));
  }

  const dismiss = React.useCallback((key: string): void => {
    const timer = timersRef.current.get(key);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(key);
    }
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  // Turn new job events (start / finish) into disposable toasts - once each, then auto-close.
  React.useEffect(() => {
    const fresh: Toast[] = [];
    for (const n of notifications) {
      const key =
        n.status === 'running'
          ? `${n.uuid}:running`
          : n.status === 'success' || n.status === 'failed'
            ? `${n.uuid}:done`
            : null;
      if (!key || announcedRef.current.has(key)) continue;
      announcedRef.current.add(key);
      fresh.push({
        key,
        title: n.title,
        meta:
          n.status === 'running'
            ? 'In progress'
            : `${n.status === 'failed' ? 'Failed' : 'Done'} - ${elapsed(n.createdAt)}`,
        variant: n.status === 'running' ? 'running' : n.severity,
      });
    }
    if (fresh.length === 0) return;
    persistAnnounced(announcedRef.current);
    setToasts((prev) => [...prev, ...fresh]);
    for (const t of fresh) {
      timersRef.current.set(t.key, window.setTimeout(() => dismiss(t.key), AUTO_CLOSE_MS));
    }
  }, [notifications, dismiss]);

  // Clear any pending timers on unmount.
  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
    };
  }, []);

  if (!enabled || toasts.length === 0) return null;

  return (
    <div className="job-toaster" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.key} className={`job-chip job-chip--${t.variant}`}>
          <div className="job-chip__head">
            <span className={`job-chip__dot job-chip__dot--${t.variant}`} aria-hidden="true" />
            <span className="job-chip__title">{t.title}</span>
            <button
              type="button"
              className="job-chip__close"
              aria-label="Dismiss"
              onClick={() => dismiss(t.key)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <span className="job-chip__meta">{t.meta}</span>
          <span className="job-chip__timer" aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}
